import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import '../Style/Earlylife.css';
import { useLanguage } from '../Context/Languagecontext';

// ── Image imports ──
import educationImg  from '../Assets/founder.webp';
import businessImg   from '../Assets/addressing.webp';
import socialImg     from '../Assets/service5.webp';
import charitableImg from '../Assets/d1.webp';
import politicalImg  from '../Assets/champ.jpg';

function EarlyyyPlaceholderIcon() {
  return (
    <svg className="earlyyy-img-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21 15 16 10 5 21"/>
    </svg>
  );
}

export default function EarlyLife() {
  const sectionRef   = useRef(null);
  const [current,    setCurrent]    = useState(0);
  const [headerVis,  setHeaderVis]  = useState(false);
  const [navVis,     setNavVis]     = useState(false);
  const [modalOpen,  setModalOpen]  = useState(false);

  const { t, lang } = useLanguage();
  const elData = t.earlyLife;

  /* ── Intersection Observer ── */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeaderVis(true);
          setTimeout(() => setNavVis(true), 400);
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* ── Localized Structural Chapters Array Mapping ── */
  const chapters = useMemo(() => {
    return [
      {
        id: 1,
        num: 'I',
        category: elData?.chapters?.ch1?.category || 'Educational Service',
        title: elData?.chapters?.ch1?.title || 'Patron of Education',
        tagline: elData?.chapters?.ch1?.tagline || 'Building Futures, Shaping Generations',
        body: elData?.chapters?.ch1?.body || '',
        quote: elData?.chapters?.ch1?.quote || '',
        traits: elData?.chapters?.ch1?.traits || null,
        image: educationImg,
        expandTitle: elData?.chapters?.ch1?.expandTitle || '',
        expandContent: elData?.chapters?.ch1?.expandContent || [],
      },
      {
        id: 2,
        num: 'II',
        category: elData?.chapters?.ch2?.category || 'Business Achievement',
        title: elData?.chapters?.ch2?.title || "The Entrepreneur's Entrepreneur",
        tagline: elData?.chapters?.ch2?.tagline || 'Creating Businesses, Not Just Businesses',
        body: elData?.chapters?.ch2?.body || '',
        quote: elData?.chapters?.ch2?.quote || '',
        traits: elData?.chapters?.ch2?.traits || null,
        image: businessImg,
        expandTitle: elData?.chapters?.ch2?.expandTitle || '',
        expandContent: elData?.chapters?.ch2?.expandContent || [],
      },
      {
        id: 3,
        num: 'III',
        category: elData?.chapters?.ch3?.category || 'Social Service',
        title: elData?.chapters?.ch3?.title || 'Serving the Community',
        tagline: elData?.chapters?.ch3?.tagline || 'A Guardian to the Elderly, A Strength to the Needy',
        body: elData?.chapters?.ch3?.body || '',
        quote: elData?.chapters?.ch3?.quote || '',
        traits: elData?.chapters?.ch3?.traits || null,
        image: socialImg,
        expandTitle: elData?.chapters?.ch3?.expandTitle || '',
        expandContent: elData?.chapters?.ch3?.expandContent || [],
      },
      {
        id: 4,
        num: 'IV',
        category: elData?.chapters?.ch4?.category || 'Charitable & Religious',
        title: elData?.chapters?.ch4?.title || 'Faith, Heritage & Community',
        tagline: elData?.chapters?.ch4?.tagline || 'Preserving Sacred Spaces, Building Common Ones',
        body: elData?.chapters?.ch4?.body || '',
        quote: elData?.chapters?.ch4?.quote || '',
        traits: elData?.chapters?.ch4?.traits || null,
        image: charitableImg,
        expandTitle: elData?.chapters?.ch4?.expandTitle || '',
        expandContent: elData?.chapters?.ch4?.expandContent || [],
      },
      {
        id: 5,
        num: 'V',
        category: elData?.chapters?.ch5?.category || 'Political & Labor',
        title: elData?.chapters?.ch5?.title || 'Leadership Beyond Business',
        tagline: elData?.chapters?.ch5?.tagline || 'A Dynamic Man of Action',
        body: elData?.chapters?.ch5?.body || '',
        quote: elData?.chapters?.ch5?.quote || '',
        traits: elData?.chapters?.ch5?.traits || null,
        image: politicalImg,
        expandTitle: elData?.chapters?.ch5?.expandTitle || '',
        expandContent: elData?.chapters?.ch5?.expandContent || [],
      },
    ];
  }, [elData]);

  const total = chapters.length;

  const goTo = useCallback((idx) => {
    const clamped = Math.max(0, Math.min(total - 1, idx));
    setCurrent(clamped);
  }, [total]);

  /* ── Key and Keyboard Trap Handling ── */
  useEffect(() => {
    const onKey = (e) => {
      if (modalOpen) {
        if (e.key === 'Escape') setModalOpen(false);
        return;
      }
      if (e.key === 'ArrowRight') goTo(current + 1);
      if (e.key === 'ArrowLeft')  goTo(current - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, goTo, modalOpen]);

  /* ── Swipe Control Bounds ── */
  const touchStartX = useRef(null);
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) goTo(diff > 0 ? current + 1 : current - 1);
    touchStartX.current = null;
  };

  const progressWidth = `${((current + 1) / total) * 100}%`;
  const ch = chapters[current];

  return (
    <section className={`earlyyy-section${lang === 'ta' ? ' earlyyy-section--tamil' : ''}`} id="early-life" ref={sectionRef}>

      <div className="earlyyy-bg-orb earlyyy-bg-orb--1" />
      <div className="earlyyy-bg-orb earlyyy-bg-orb--2" />
      <div className="earlyyy-bg-line" />

      {/* ── Header ── */}
      <header className={`earlyyy-header${headerVis ? ' visible' : ''}`}>
        <span className="earlyyy-eyebrow">{elData?.eyebrow}</span>
        <h2 className="earlyyy-title">{elData?.titlePre}<em>{elData?.titleEm}</em></h2>
        <p className="earlyyy-subtitle">{elData?.subtitle}</p>
      </header>

      {/* ── Slider ── */}
      <div
        className="earlyyy-slider-outer"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        role="region"
        aria-label="Early life chapters"
      >
        <div
          className="earlyyy-track"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {chapters.map((c, i) => (
            <div
              key={c.id}
              className={`earlyyy-slide${i === current ? ' active' : ''}`}
              aria-hidden={i !== current}
            >
              {/* ── Image Panel ── */}
              <div className="earlyyy-image-panel">
                {c.image ? (
                  <img src={c.image} alt={c.title} loading="lazy" />
                ) : (
                  <div className="earlyyy-img-placeholder">
                    <div className="earlyyy-img-placeholder-inner">
                      <EarlyyyPlaceholderIcon />
                      <p>{elData?.placeholderText || "Photo"}</p>
                    </div>
                  </div>
                )}
                <div className="earlyyy-image-overlay" />

                <div className="earlyyy-img-badge">
                  <div className="earlyyy-img-badge-num">{c.num}</div>
                </div>

                <div className="earlyyy-img-year">{c.year}</div>
                <div className="earlyyy-img-cat">{c.category}</div>
              </div>

              {/* ── Text Panel ── */}
              <div className="earlyyy-text-panel" data-num={c.num}>
                <div className="earlyyy-panel-corner" />

                <span className="earlyyy-chapter-label">{c.category}</span>
                <h3 className="earlyyy-slide-title">{c.title}</h3>
                <p className="earlyyy-slide-tagline">{c.tagline}</p>

                <div className="earlyyy-divider" />

                <p className="earlyyy-slide-body">{c.body}</p>

                {c.traits && (
                  <ul className="earlyyy-traits">
                    {c.traits.map((t, ti) => (
                      <li key={ti} className="earlyyy-trait">{t}</li>
                    ))}
                  </ul>
                )}

                <div className="earlyyy-quote">
                  <span className="earlyyy-quote-mark">"</span>
                  <p>{c.quote ? c.quote.replace(/^"|"$/g, '') : ''}</p>
                </div>

                <button
                  className="earlyyy-viewmore-btn"
                  onClick={() => setModalOpen(true)}
                  aria-label={elData?.controls?.viewMoreDetails || "View more details"}
                >
                  <span>{elData?.controls?.viewMore || "View More"}</span>
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M4 10h12M10 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="earlyyy-progress">
          <div className="earlyyy-progress-fill" style={{ width: progressWidth }} />
        </div>
      </div>

      {/* ── Navigation ── */}
      <nav className={`earlyyy-nav${navVis ? ' visible' : ''}`} aria-label={elData?.controls?.navLabel || "Slide navigation"}>
        <button
          className="earlyyy-nav-btn"
          onClick={() => goTo(current - 1)}
          disabled={current === 0}
          aria-label={elData?.controls?.prevChapter || "Previous chapter"}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>

        <div className="earlyyy-dots">
          {chapters.map((c, i) => (
            <button
              key={c.id}
              className={`earlyyy-dot-item${i === current ? ' active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`${elData?.controls?.goTo || "Go to"} ${c.title}`}
              aria-current={i === current ? 'true' : undefined}
            >
              <div className="earlyyy-dot" />
              <span className="earlyyy-dot-label">{c.num}</span>
            </button>
          ))}
        </div>

        <button
          className="earlyyy-nav-btn"
          onClick={() => goTo(current + 1)}
          disabled={current === total - 1}
          aria-label={elData?.controls?.nextChapter || "Next chapter"}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </nav>

      {/* ── Chapter counter ── */}
      <div className={`earlyyy-counter${navVis ? ' visible' : ''}`}>
        <span className="earlyyy-counter-current">{String(current + 1).padStart(2, '0')}</span>
        <span className="earlyyy-counter-sep">/</span>
        <span className="earlyyy-counter-total">{String(total).padStart(2, '0')}</span>
      </div>

      {/* ── View More Modal ── */}
      {modalOpen && (
        <div className="earlyyy-modal-backdrop" onClick={() => setModalOpen(false)}>
          <div className="earlyyy-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="earlyyy-modal-accent" />

            <button className="earlyyy-modal-close" onClick={() => setModalOpen(false)} aria-label={elData?.controls?.close || "Close"}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>

            <span className="earlyyy-modal-cat">{ch.category}</span>
            <h3 className="earlyyy-modal-title">{ch.expandTitle}</h3>
            <div className="earlyyy-modal-divider" />

            <div className="earlyyy-modal-body">
              {ch.expandContent.map((para, i) => (
                <p key={i} className="earlyyy-modal-para">{para}</p>
              ))}
            </div>

            <div className="earlyyy-modal-footer">
              <button className="earlyyy-modal-nav" onClick={() => goTo(current - 1)} disabled={current === 0}>
                {elData?.controls?.prevBtn || "← Previous"}
              </button>
              <span className="earlyyy-modal-indicator">{ch.year}</span>
              <button className="earlyyy-modal-nav" onClick={() => goTo(current + 1)} disabled={current === total - 1}>
                {elData?.controls?.nextBtn || "Next →"}
              </button>
            </div>
          </div>
        </div>
      )}

    </section>
  );
}